import React, { useRef, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { uploadImage } from '../utils/api';
import { toast } from 'sonner';

// Get the backend URL from environment variable
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const RichTextEditor = ({ value, onChange, placeholder = "Write your content here...", minHeight = "200px" }) => {
  const quillRef = useRef(null);

  // Image handler for uploading images
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be smaller than 5MB');
        return;
      }

      try {
        toast.loading('Uploading image...', { id: 'image-upload' });
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await uploadImage(formData);
        // Construct full URL using the backend URL (LexDoRight website)
        const relativeUrl = response.data.file_url;
        const imageUrl = `${BACKEND_URL}${relativeUrl}`;

        // Insert image into editor
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', imageUrl);
          quill.setSelection(range.index + 1);
        }

        toast.success('Image uploaded successfully!', { id: 'image-upload' });
      } catch (error) {
        console.error('Image upload error:', error);
        toast.error('Failed to upload image', { id: 'image-upload' });
      }
    };
  };

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        // Font size dropdown
        [{ 'size': ['small', false, 'large', 'huge'] }],
        // Headers
        [{ 'header': [1, 2, 3, false] }],
        // Text formatting
        ['bold', 'italic', 'underline', 'strike'],
        // Color
        [{ 'color': [] }, { 'background': [] }],
        // Lists
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        // Alignment
        [{ 'align': [] }],
        // Links and images
        ['link', 'image'],
        // Clear formatting
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  // Quill formats
  const formats = [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link', 'image'
  ];

  return (
    <div className="rich-text-editor">
      <style>{`
        .rich-text-editor .ql-container {
          min-height: ${minHeight};
          font-size: 16px;
          font-family: inherit;
        }
        .rich-text-editor .ql-editor {
          min-height: ${minHeight};
        }
        .rich-text-editor .ql-toolbar {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          background: #f8fafc;
          border-color: #e2e8f0;
        }
        .rich-text-editor .ql-container {
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          border-color: #e2e8f0;
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: #94a3b8;
          font-style: normal;
        }
        .rich-text-editor .ql-snow .ql-picker.ql-size .ql-picker-label::before,
        .rich-text-editor .ql-snow .ql-picker.ql-size .ql-picker-item::before {
          content: 'Normal';
        }
        .rich-text-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="small"]::before,
        .rich-text-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="small"]::before {
          content: 'Small';
        }
        .rich-text-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="large"]::before,
        .rich-text-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="large"]::before {
          content: 'Large';
        }
        .rich-text-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="huge"]::before,
        .rich-text-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="huge"]::before {
          content: 'Huge';
        }
        .rich-text-editor .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 8px 0;
        }
        .rich-text-editor .ql-snow .ql-tooltip {
          z-index: 9999;
        }
      `}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;
